import { useState, type InputHTMLAttributes } from 'react';

interface PasswordFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string | null;
  /**
   * Requirement copy. Belongs to the field rather than sitting loose beneath
   * it, so `aria-describedby` reads it out when the field takes focus —
   * before the user commits to a password, not after it is rejected.
   */
  hint?: string;
  /** Copy for the show/hide toggle. */
  showLabel: string;
  hideLabel: string;
}

export function PasswordField({
  id,
  label,
  error,
  hint,
  showLabel,
  hideLabel,
  ...rest
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-buds-dark">
        {label}
      </label>
      <div className="relative">
        <input
          {...rest}
          id={id}
          type={visible ? 'text' : 'password'}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={[
            'mt-2 h-12 w-full rounded-xl border bg-buds-surface pl-4 pr-20 text-base text-buds-dark',
            'focus-ring',
            error ? 'border-buds-danger' : 'border-buds-muted/80',
          ].join(' ')}
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          // A toggle, so its state is announced rather than inferred from a
          // label that only changes after the fact.
          aria-pressed={visible}
          aria-controls={id}
          className="focus-ring absolute right-3 top-2 h-12 px-2 text-sm font-medium text-buds-dark underline underline-offset-4"
        >
          {visible ? hideLabel : showLabel}
        </button>
      </div>
      {error ? (
        <p id={errorId} className="mt-2 text-sm text-buds-danger">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="mt-2 text-sm leading-relaxed text-buds-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
